import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import cloneDeep from 'lodash.clonedeep';

import { FORMAT_DEFAULT, FUND_CHART_SYSTEM_DEFAULT_STATE } from 'ChartApp/ts/features/fundamental-chart/constants';
import { ChartDateZoom } from 'ChartApp/ts/enums/chart-common.enum';
import {
    FundamentalChartData,
    FundChartConfigurableOptions,
    FundChartSecurity,
    FundChartState,
    FundChartZoomOption,
} from 'ChartApp/ts/interfaces/fund-chart.interface';

import { ChartZoomService } from 'ChartApp/ts/features/charts-shared/services/chart-zoom.service';
import { FundChartService } from 'ChartApp/ts/services/fund-chart.service';

@Component({
    standalone: false,
    selector: 'ycn-simple-fund-chart',
    templateUrl: './simple-fund-chart.template.html',
    providers: [ChartZoomService],
})
export class SimpleFundChartComponent implements OnInit, OnChanges {
    @Input() securityIds: string[] = [];

    @Input() calcs: string[] = [];

    @Input() format: string = FORMAT_DEFAULT;

    @Input() zoom: ChartDateZoom | '' = '';

    @Input() showRecessions: boolean = false;

    @Input() initialChartState: FundChartState | null = null;

    configurableOptions: FundChartConfigurableOptions = {
        dynamicHeight: false,
        showLatestValueLabels: true,
        tooltipStyle: 'box',
        yAxisWidth: 60,
        tooltipCallback: (data: object[]) => data,
    };

    chartOptions: object = {
        xAxis: {
            tickAmount: 4,
        },
    };

    loadingChart: boolean = true;

    noDataClass: string = 'get-started-border';

    chartDataUrl: string = '';

    currentZoom: ChartDateZoom | '' = '';

    tooltipData: object[] = [];

    subchartOffsets: number[] = [];

    state: FundChartState = cloneDeep(FUND_CHART_SYSTEM_DEFAULT_STATE);

    constructor(private changeDetector: ChangeDetectorRef, private fundChartService: FundChartService) {}

    ngOnInit(): void {
        if (this.initialChartState) {
            // Use full state that was passed
            this.state = cloneDeep(this.initialChartState);
        } else {
            // Construct state from default state plus inputs
            const calcs = [];
            const securities = [];

            for (let i = 0; i < this.calcs.length; i += 1) {
                calcs.push({
                    id: this.calcs[i],
                    include: true,
                });
            }

            for (let i = 0; i < this.securityIds.length; i += 1) {
                securities.push({
                    id: this.securityIds[i],
                    include: true,
                });
            }

            this.state.calcs = calcs;
            this.state.format = this.format;
            this.state.securities = securities as FundChartSecurity[];
            this.state.zoom = this.zoom || FUND_CHART_SYSTEM_DEFAULT_STATE.zoom;
        }

        this.setChartDataUrls();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.showRecessions) {
            this.state.recessions = this.showRecessions;
            this.configurableOptions.recessions = this.showRecessions;
            this.setChartDataUrls();
        }
    }

    setChartDataUrls(): void {
        this.currentZoom = this.state.zoom;
        this.chartDataUrl = this.fundChartService.getChartDataUrl(this.state);
    }

    getQueryParams(): string {
        return this.fundChartService.getChartDataUrlQuery(this.state);
    }

    setZoomValue(zoomOption: FundChartZoomOption): void {
        this.state.zoom = zoomOption.value;
        this.setChartDataUrls();
    }

    chartDataHandler(chartData: FundamentalChartData): void {
        this.loadingChart = !(chartData && Array.isArray(chartData.chart_data) && chartData.chart_data.length > 0);

        // Manually detect changes here
        this.changeDetector.detectChanges();
    }

    subchartOffsetsHandler(subchartOffsets: number[]): void {
        this.subchartOffsets = subchartOffsets;
    }

    tooltipDataHandler(tooltipData: object[]): void {
        this.tooltipData = tooltipData;
    }
}
